import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil.ts';
import { useMemo } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetActivePracticeAreasWithFiltersQuery } from 'store/api/practice-areas';
import { ClassifierData } from 'types/classifiers';
import { PartyType } from 'types/contacts';
import { NOMINATION_STATUSES } from 'types/efforts';
import { t } from 'utils/getTranslations';

const GROUP = 'rankings-referees.filter-groups.nomination';

/**
 * Requested this year: an effort dated within the current calendar year, computed in the browser as the invoices
 * list computes its dates. The quick filter and the panel's checkbox share a key, so the panel's clause is the one
 * sent, both bounds of the year. Unparenthesised: the URL's filter is read back split on `;`, and its first clause
 * then selects the checkbox again.
 */
export const requestedThisYear = (today: Date = new Date()) => {
    const year = today.getFullYear();
    return {
        argument: `${year}-01-01`,
        rsql: `requestedOn=ge=${year}-01-01;requestedOn=le=${year}-12-31`,
    };
};

const REQUESTED_THIS_YEAR = {
    label: 'rankings-referees.filter.requested-this-year',
    tooltip: 'rankings-referees.filter.requested-this-year-tooltip',
};

type Args = {
    classifiers: Record<string, ClassifierData>;
    /**
     * The contact whose nominations the list shows: its search reads the practice area and the requester, and it
     * has no Referee filter.
     */
    contactId?: string;
};

/** The Rankings referees list's quick filters, filter panel, search and the filter it always applies. */
export function useRankingsRefereeFilterConfig({ classifiers, contactId }: Args): FilterConfig {
    return useMemo(() => {
        const thisYear = requestedThisYear();
        const year = new Date().getFullYear();
        return {
            quickFilters: [
                {
                    selector: 'requestedOn',
                    comperator: '=ge=',
                    filters: [
                        {
                            label: t(REQUESTED_THIS_YEAR.label),
                            tooltip: REQUESTED_THIS_YEAR.tooltip,
                            argument: thisYear.argument,
                        },
                    ],
                },
                // A value of the Status filter, which it stays in step with.
                {
                    selector: 'statusCode',
                    filters: [
                        {
                            label: t('rankings-referees.filter.waiting'),
                            tooltip: 'rankings-referees.filter.waiting-tooltip',
                            argument: 'REQUESTED',
                        },
                    ],
                },
            ],
            filters: [
                ...(contactId
                    ? []
                    : [
                          {
                              type: 'dynamicmultiselect' as const,
                              subType: 'contact' as const,
                              label: t('rankings-referees.filter.referee'),
                              tooltip: 'rankings-referees.filter.referee-tooltip',
                              selector: 'referee.id',
                              group: GROUP,
                              paginated: {
                                  fetch: useLazyGetMinContactsListWithFiltersQuery,
                                  searchField: ['name', 'partyNumber'],
                                  filters: `partyType==${PartyType.PERSON}`,
                                  getOptionIdentifier: 'id',
                                  getOptionLabel: 'name',
                              },
                          },
                      ]),
                {
                    type: 'checkboxgroup',
                    selector: 'requestedOn',
                    comperator: '=ge=',
                    group: GROUP,
                    filters: [
                        {
                            label: t(REQUESTED_THIS_YEAR.label),
                            tooltip: REQUESTED_THIS_YEAR.tooltip,
                            argument: thisYear.argument,
                            rsqlOverride: thisYear.rsql,
                        },
                    ],
                },
                {
                    type: 'multiselect',
                    label: t('rankings-referees.filter.directory'),
                    tooltip: 'rankings-referees.filter.directory-tooltip',
                    selector: 'legalDirectoryCode',
                    group: GROUP,
                    options: getFilterOptionsFromClassifier(classifiers['LEGAL_DIRECTORY']),
                },
                {
                    type: 'multiselect',
                    label: t('rankings-referees.filter.edition'),
                    tooltip: 'rankings-referees.filter.edition-tooltip',
                    selector: 'editionYear',
                    group: GROUP,
                    // Next year's edition, the one a request made this year defaults to, and the four before it.
                    options: Array.from({ length: 5 }, (_, back) => String(year + 1 - back)).map((edition) => ({
                        label: edition,
                        value: edition,
                    })),
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('rankings-referees.filter.practice-area'),
                    tooltip: 'rankings-referees.filter.practice-area-tooltip',
                    selector: 'practiceArea.id',
                    group: GROUP,
                    nullOption: { label: t('rankings-referees.not-set') },
                    paginated: {
                        fetch: useLazyGetActivePracticeAreasWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('rankings-referees.filter.status'),
                    tooltip: 'rankings-referees.filter.status-tooltip',
                    selector: 'statusCode',
                    group: GROUP,
                    options: NOMINATION_STATUSES.map((status) => ({
                        label: t(`rankings-referees.status.${status}`),
                        value: status,
                    })),
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('rankings-referees.filter.requested-by'),
                    tooltip: 'rankings-referees.filter.requested-by-tooltip',
                    selector: 'requestedBy.id',
                    group: GROUP,
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
            ],
            searchBarSelectors: contactId
                ? [{ selector: 'practiceArea.name' }, { selector: 'requestedBy.fullName' }]
                : [{ selector: 'referee.name' }, { selector: 'referee.companyNames' }],
            passiveFilter: contactId ? `referee.id==${contactId}` : '',
        };
    }, [classifiers, contactId]);
}
